import {
    OptionItemState,
    TextContentElement,
} from '@deltek/specification-client/core/Models/textContentElement'
import { SuggestionData } from 'ckeditor5-premium-features'
import { ObjectBuilder } from 'components/WorkspaceEditor/factories'
import { TextElementParser } from 'components/WorkspaceEditor/services/TextElementParser'
import type { NodeType } from 'components/WorkspaceEditor/types'
import { nullOrUndefinedResolver } from 'components/WorkspaceEditor/utils'
import { TrackChangeType } from 'models/changes'
import {
    PROPERTY_EDITOR_EDITABLE_TEXT_VARIANT as NodeTypes,
    SuggestionTypeLookup,
} from 'pages/ProjectsPage/WorkspacePage/components/constants'
import { v4 as newGuid } from 'uuid'

export const HIGHLIGHT_CLASS = 'card-highlight'
const TRACK_CHANGES_CARD_CLASS = 'trackChangesCard'
const ACCEPT_BUTTON_CLASS = 'acceptIconButton'
const DECLINE_BUTTON_CLASS = 'discardIconButton'
const HIGHLIGHT_ACCEPT_CLASS = 'card-highlight-accept-button'
const HIGHLIGHT_DECLINE_CLASS = 'card-highlight-decline-button'
const CKE_EVALUATION_BADGE_SELECTOR =
    '.ck.ck-balloon-panel.ck-balloon-panel_visible'

/**
 * Preserves leading and trailing whitespace by converting spaces to &nbsp; entities.
 * This prevents CKEditor from trimming spaces at the beginning and end of text.
 * @param text - The text to process
 * @returns The text with preserved leading and trailing whitespace
 */
const preserveWhitespace = (text: string | undefined): string => {
    if (!text) return text || ''

    let preserved = text.replace(/^( +)/, (match) =>
        '&nbsp;'.repeat(match.length)
    )

    preserved = preserved.replace(/( +)$/, (match) =>
        '&nbsp;'.repeat(match.length)
    )

    return preserved
}


/**
 * Prevents CKEditor from trimming trailing spaces while allowing line breaks.
 * Replaces trailing &nbsp; or spaces with space + zero-width space.
 */
export const preserveTrailingSpaceWithoutNbsp = (html?: string): string => {
    if (!html) return ''
    // Replace leading &nbsp; with zero-width space + regular space
    let cleaned = html.replace(/(<span[^>]*>)(&nbsp;|\s)+/g, '$1\u200B ')
    // Replace trailing &nbsp; with regular space + zero-width space
    cleaned = cleaned.replace(/(&nbsp;|\s)+(<\/span><\/p>)/g, ' \u200B$2')
    return cleaned
}

/**
 * Converts an array of text content elements to a string representing editor data.
 * @param textContentElements - The array of text content elements.
 * @param isInsideOptionItem - Indicates if the elements are inside an option item.
 * @param optionGroupId - The ID of the option group we are in, if any.
 * @returns The string representing the editor data.
 */
export const elementsToEditorData = (
    textContentElements: Partial<TextContentElement>[] | undefined,
    isInsideOptionItem?: boolean,
    optionGroupId?: string
): string => {
    return textContentElements
        ? textContentElements
              .map((el) =>
                  elementToEditorData(el, isInsideOptionItem, optionGroupId)
              )
              .join('')
        : ''
}

/**
 * Converts a text content element to a string/html format representing editor data.
 * @param textContentElement - The text content element.
 * @param isInsideOptionItem - Indicates if the element is inside an option item.
 * @param optionGroupId - The ID of the option group we are in, if any.
 * @returns The string/html format representing the editor data.
 */
export const elementToEditorData = (
    textContentElement: Partial<TextContentElement>,
    isInsideOptionItem: boolean = false,
    optionGroupId: string = ''
) => {
    if (!textContentElement) {
        return ''
    }

    const optionGroupIdAttribute = optionGroupId
        ? ` data-option-group-id='${optionGroupId}'`
        : ''

    const processedText = preserveWhitespace(textContentElement.text)

    switch (textContentElement.type as NodeType) {
        case NodeTypes.Text:
            const suggestionName = getSuggestionName(textContentElement)
            const startChangeTag = suggestionName
                ? `<suggestion-start name='${suggestionName}'></suggestion-start>`
                : ''
            const endChangeTag = suggestionName
                ? `<suggestion-end name='${suggestionName}'></suggestion-end>`
                : ''
            return processedText
                ? `<p><span class='paragraph-text' data-id='${textContentElement.id}'${optionGroupIdAttribute}>${startChangeTag}${processedText}${endChangeTag}</span></p>`
                : ''
        case NodeTypes.Hyperlink:
            return `<a href='${textContentElement.hyperlinkUrl}' data-id='${textContentElement.id}'${optionGroupIdAttribute}>${processedText}</a>`
        case NodeTypes.UnitsOfMeasure:
            return `<span class='${
                isInsideOptionItem
                    ? 'nested-units-of-measure'
                    : 'units-of-measure'
            }' data-id='${
                textContentElement.id
            }'${optionGroupIdAttribute}><span class='imperial'>${
                textContentElement.imperialUnits
            }</span><span class='metric'>${
                textContentElement.metricUnits
            }</span></span>`
        case NodeTypes.OptionGroup:
            return elementsToEditorData(
                textContentElement.elements,
                false,
                textContentElement.id
            )
        case NodeTypes.SelectionOption:
            return `<span class='bracket-option' data-id='${
                textContentElement.id
            }'${optionGroupIdAttribute} data-opted-state='${getOptedState(
                textContentElement.optionItemState
            )}' data-editable='false'>${processedText}${elementsToEditorData(
                textContentElement.elements,
                true
            )}</span>`
        case NodeTypes.ReplacementOption:
            return `<span class='bracket-option' data-id='${
                textContentElement.id
            }'${optionGroupIdAttribute} data-opted-state='${getOptedState(
                textContentElement.optionItemState
            )}' data-editable='true'>${processedText}</span>`
        default:
            return ''
    }
}

/**
 * Gets the author ID for a suggestion.
 * - If provided and not empty, it is trimmed and returned.
 * - If not provided, empty, or whitespace-only, 'anonymous1' is returned.
 * @param authorId - The author ID to resolve.
 * @returns The resolved and trimmed author ID or 'anonymous1'.
 */
export const resolveAuthorId = (authorId: string | null | undefined): string =>
    authorId?.trim() || 'anonymous1'

/**
 * Gets the name attribute to assign to a suggestion tag for loading into CKEditor.
 * @param textContentElement - The text content element.
 * @returns The name attribute for the suggestion tag or an empty string if no suggestion is needed.
 */
const getSuggestionName = (
    textContentElement: Partial<TextContentElement>
): string => {
    const suggestionType = getSuggestionType(textContentElement.changeType)
    return suggestionType
        ? `${suggestionType}:${textContentElement.id}:${resolveAuthorId(
              textContentElement.authoredUserId
          )}`
        : ''
}

/**
 * Converts between change type and suggestion type.
 *
 * This function converts a given change type (e.g., 'ADDED', 'DELETED')
 * to the corresponding suggestion type expected by CKEditor (e.g., 'insertion', 'deletion'),
 * and vice versa. For example, if the input is 'ADDED', it returns 'insertion';
 * if the input is 'insertion', it returns 'ADDED'. The conversion is case-insensitive.
 *
 * @param {string | null | undefined} changeType - The change type or suggestion type.
 * @returns {string} - The converted type. Returns an empty string if the input is unrecognized.
 */
const getSuggestionType = (changeType: string | null | undefined): string => {
    if (!changeType) return ''

    const key = changeType.toUpperCase()
    return SuggestionTypeLookup[key] || ''
}

/**
 * Gets the opted state as a string based on the option item state.
 * @param optionItemState - The option item state.
 * @returns The string representing the opted state.
 */
const getOptedState = (
    optionItemState: OptionItemState | null | undefined
): string => {
    switch (optionItemState) {
        case 'OptedIn':
            return 'OPTED_IN'
        case 'OptedOut':
            return 'OPTED_OUT'
        case 'InActive':
            return 'OPTED_OUT' // TODO: determine if this is correct
        default:
            return 'UNDECIDED'
    }
}

/**
 * Extracts track change suggestions from an array of text content elements.
 * @param textContentElements - The array of text content elements to extract suggestions from.
 * @returns An array of track change suggestions for CKEditor.
 */
export const extractSuggestions = (
    textContentElements: Partial<TextContentElement>[] | undefined
): SuggestionData[] => {
    const isTrackChange = (element?: Partial<TextContentElement>): boolean =>
        ['ADDED', 'DELETED', 'MODIFIED', 'DISABLED'].includes(
            element?.changeType?.toUpperCase() ?? ''
        )

    /** Maps a text content element to a SuggestionData object.
     * - changeType elements should always have authoredUserId,
     * - use `anonymous1' as default if it has blank id.
     * TODO: createdAt value should come from TC api
     */
    const toSuggestionData = (
        element: Partial<TextContentElement>
    ): SuggestionData =>
        new ObjectBuilder<SuggestionData>()
            .resolveWith(nullOrUndefinedResolver)
            .set('id', element.id)
            .set('type', getSuggestionType(element.changeType))
            .set('authorId', resolveAuthorId(element.authoredUserId))
            .set('attributes', {})
            .set('createdAt', new Date())
            .set('data', {
                key: element.id ?? '',
                oldValue: element.text,
                newValue: element.text,
            })
            .build()

    return (
        textContentElements?.filter(isTrackChange).map(toSuggestionData) ?? []
    )
}

/**
 * Converts editor data to an array of text content elements.
 * @param editorData - The editor data to convert.
 * @returns An array of text content elements.
 */
export const editorDataToElements = (
    editorData: string
): Partial<TextContentElement>[] => {
    const elements: Partial<TextContentElement>[] = []
    const parser = new DOMParser()
    const doc = parser.parseFromString(editorData, 'text/html')
    const paragraph = doc.querySelector('p')
    if (!paragraph) {
        return elements
    }

    const childNodes = paragraph.childNodes
    for (let i = 0; i < childNodes.length; i++) {
        const node = childNodes[i]
        const textElement = convertNodeToElement(node)
        if (textElement) {
            elements.push(textElement)
        }
    }

    return elements
}

export const convertNodeToElement = (
    node: Node
): Partial<TextContentElement> | null => {
    const htmlElement = node as HTMLElement
    if (!htmlElement) {
        return null
    }

    const dataId = htmlElement.getAttribute('data-id')
    const textElement = createTextContentElement()
    textElement.id = dataId || ''
    if (node.nodeType === Node.ELEMENT_NODE && node.nodeName === 'SPAN') {
        if (htmlElement.className === 'paragraph-text') {
            textElement.text = htmlElement.textContent || ''
            return textElement
        } else if (
            htmlElement.className === 'units-of-measure' ||
            htmlElement.className === 'nested-units-of-measure'
        ) {
            const unitsOfMeasure = htmlElement.children
            textElement.type = 'UnitsOfMeasure'
            textElement.imperialUnits = unitsOfMeasure[0].textContent || ''
            textElement.metricUnits = unitsOfMeasure[1].textContent || ''
            return textElement
        } else if (htmlElement.className === 'bracket-option') {
            const optedState = htmlElement.getAttribute('data-opted-state')
            const isEditable =
                htmlElement.getAttribute('data-editable') === 'true'
            textElement.type = isEditable
                ? 'ReplacementOption'
                : 'SelectionOption'
            textElement.text = htmlElement.textContent || ''
            textElement.isOptionItem = true
            textElement.optionItemState = getOptionItemState(optedState)
            return textElement
        }
    } else if (node.nodeType === Node.ELEMENT_NODE && node.nodeName === 'A') {
        const a = node as HTMLAnchorElement
        textElement.type = 'Hyperlink'
        textElement.text = a.textContent || ''
        textElement.hyperlinkUrl = a.href.replace(/\/$/, '') // Remove trailing slash that DOMParser adds for some reason
        return textElement
    }

    return null
}

const createTextContentElement = (): TextContentElement => {
    return {
        id: '',
        type: 'Text',
        text: '',
        isOptionItem: false,
        imperialUnits: '',
        metricUnits: '',
        fieldId: '',
        elements: [],
        optionItemState: null,
        hyperlinkUrl: '',
        placeholderText: '',
        changeType: null,
        authoredUserId: '',
    }
}

const getOptionItemState = (optedState: string | null): OptionItemState => {
    switch (optedState) {
        case 'OPTED_IN':
            return 'OptedIn'
        case 'OPTED_OUT':
            return 'OptedOut'
        case 'UNDECIDED':
            return null
        default:
            return null
    }
}

export const removeDeletionMarkers = (html: string = '') => {
    if (!html) return ''
    if (typeof DOMParser === 'undefined') {
        // Fallback: use regex similar to sanitizeAndExtractText to remove deletion suggestion pairs
        return html
            .replace(
                /<suggestion-start[^>]*name="deletion:[^"]*"[^>]*>[\s\S]*?<suggestion-end[^>]*name="deletion:[^"]*"[^>]*>/g,
                ''
            )
            .replace(
                /<suggestion-start[^>]*name="deletion:[^"]*"[^>]*\/?\s*>/g,
                ''
            )
            .replace(
                /<suggestion-end[^>]*name="deletion:[^"]*"[^>]*\/?\s*>/g,
                ''
            )
    }

    const parser = new DOMParser()
    const doc = parser.parseFromString(html, 'text/html')

    const body = doc.body
    const deletionPairs = new Map()

    // Collect all deletion <suggestion-start> and <suggestion-end> pairs
    const allStarts = [...body.querySelectorAll('suggestion-start')]
    for (const start of allStarts) {
        const name = start.getAttribute('name')
        if (name?.startsWith('deletion:')) {
            // Find matching end
            const end = body.querySelector(`suggestion-end[name="${name}"]`)
            if (end) deletionPairs.set(start, end)
        }
    }

    // Remove text and markers between each deletion pair
    deletionPairs.forEach((end, start) => {
        const range = document.createRange()
        range.setStartAfter(start)
        range.setEndBefore(end)
        range.deleteContents() // delete the text inside
        start.remove()
        end.remove()
    })

    return body.innerHTML
}

/** Extracts the text from HTML content.
 * - if haveSuggestions is true, returns the text content including the suggestion changes.
 * - otherwise, returns the text content without suggestion changes.
 * @param html - The HTML content to extract the text from.
 * @param hasSuggestion - Indicates if the HTML content contains suggestions.
 * @returns The text content extracted from the HTML.
 */
export const sanitizeAndExtractText = (
    html: string = '',
    hasSuggestion: boolean = false
) => {
    if (!html) {
        return ''
    }

    const updatedHtml = removeDeletionMarkers(html)

    if (typeof DOMParser !== 'undefined' && hasSuggestion) {
        const parser = new DOMParser()
        // parse the input as HTML
        // Replace U+00A0 (non-breaking space) with regular space to match
        // the regex path behavior (&nbsp; → ' ') and prevent browser-specific
        // false positives in change detection (Firefox CKEditor uses U+00A0)
        return (
            parser.parseFromString(updatedHtml, 'text/html').body.textContent?.replace(/\u00A0/g, ' ') ||
            ''
        ) // automatically decodes all HTML entities
    } else {
        // fallback: use regex to strip HTML tags
        return updatedHtml
            .replace(/<suggestion-start[\s\S]*?<suggestion-end[\s\S]*?>/g, '')
            .replace(/<\/?[^>]+(>|$)/g, '')
            .replace(/&nbsp;/g, ' ') // non-breaking space entity
            .replace(/\u00A0/g, ' ') // non-breaking space character (U+00A0)
            .replace(/&lt;/g, '<')
            .replace(/&gt;/g, '>')
            .replace(/&amp;/g, '&')
            .replace(/&quot;/g, '"')
            .replace(/&#39;/g, "'") // Single quote (apostrophe)
    }
}

/** Extracts the unresolved change between editor and initial data.
 * @param editorData - The current editor data string (HTML syntax).
 * @param initialData - The initial editor data string.
 * @param hasInitialSuggestions - Indicates if the HTML content contains suggestions.
 *  Returns the text value of the unresolved change.
 */
export const getUnresolvedChanges = (
    editorData: string,
    initialData: string = '',
    hasInitialSuggestions: boolean = false
) => {
    // check if there are inline suggestions
    const hasNewSuggestions = Boolean(
        sanitizeAndExtractText(editorData, true) !== initialData
    )

    // extract the text value of the unresolved change
    const editorText = sanitizeAndExtractText(
        editorData,
        hasInitialSuggestions || hasNewSuggestions
    )

    // return the unresolved change, otherwise empty string
    return editorText !== initialData ? editorText : ''
}

/** Helper to process CKEditor’s HTML content into text content elements and assign new IDs/authoredUserId where needed.
 * - parse html content to TextElement[]
 * - assign new ID/authoredUserId if element is modified
 */
export const processContent = (
    editorHtmlContent: string,
    chatUserId: string | undefined = 'anonymous1'
) => {
    const parser = new TextElementParser()
    const textElements = parser.process(editorHtmlContent)

    return textElements.map((element) => {
        const changeTypes = new Set([
            TrackChangeType.ADDED,
            TrackChangeType.DELETED,
            TrackChangeType.MODIFIED,
            TrackChangeType.DISABLED,
        ])
        const isElementChanged =
            element.changeType !== undefined &&
            element.changeType !== null &&
            changeTypes.has(element.changeType as TrackChangeType)

        return new ObjectBuilder(element)
            .set('id', isElementChanged ? newGuid() : element.id)
            .set(
                'authoredUserId',
                isElementChanged ? chatUserId : element.authoredUserId
            )
            .build()
    })
}

/** Checks if the content item nodes contain keyed (:) content. */
export const isKeyedContent = (
    contentItemNodes: Partial<TextContentElement>[]
) => {
    return contentItemNodes.some((element) =>
        element.text?.trim().endsWith(':')
    )
}

/**
 * Hides balloon panels.
 * This is useful for preventing the balloon panels from being visible when the DOM changes.
 * It observes the parent node of the balloon panel and hides it when a child is added or removed.
 */
export const hideInLineTrackTip = () => {
    const style = document.createElement('style')

    style.textContent = `${CKE_EVALUATION_BADGE_SELECTOR} { display: none !important; }`
    document.head.appendChild(style)

    return style
}

export const getSuggestionCardsById = (
    suggestionId: string | undefined
): HTMLElement[] | null => {
    if (!suggestionId || !document?.body) return null

    const selectors = [
        `[data-testid='${CSS.escape(suggestionId)}']`,
        `[data-suggestion-id='${CSS.escape(suggestionId)}']`,
    ]
    const htmlElements: HTMLElement[] = []

    for (const selector of selectors) {
        const elements = document.querySelectorAll<HTMLElement>(selector)
        if (elements && elements.length > 0) {
            elements.forEach((element) => htmlElements.push(element))
        }
    }
    return htmlElements.length > 0 ? htmlElements : null
}

/**
 * Highlights a card by its suggestion ID by toggling a CSS class.
 * @param suggestionId - The ID of the suggestion to target.
 * @param highlightClass - The CSS class to apply for highlighting.
 */
export const highlightCard = (
    suggestionId: string | undefined,
    highlightClass: string,
    isNotDefaultSuggestionCardFilter: boolean = false
) => {
    if (!suggestionId || !document?.body) return

    // Only remove highlight from cards that are currently highlighted
    document
        .querySelectorAll<HTMLElement>(
            `.${TRACK_CHANGES_CARD_CLASS}.${highlightClass}`
        )
        .forEach((card) => {
            toggleButtonHighlightClasses(card, 'remove', highlightClass)
        })

    const cards = getSuggestionCardsById(suggestionId)
    if (!cards) return

    cards.forEach((card) => {
        if (card instanceof HTMLElement) {
            toggleButtonHighlightClasses(card, 'add', highlightClass)
        }
    })

    if (!isNotDefaultSuggestionCardFilter) return

    requestAnimationFrame(() => {
        cards?.[0].scrollIntoView({
            behavior: 'smooth',
            block: 'nearest',
            inline: 'nearest',
        })
    })
}

/**
 * Toggles highlight classes on accept and decline buttons within a track changes card.
 * @param card - The track changes card element.
 * @param action - The action to perform ('add' or 'remove').
 */
const toggleButtonHighlightClasses = (
    card: HTMLElement,
    action: 'add' | 'remove',
    highlightClass: string
) => {
    card.classList[action](highlightClass)
    card.querySelector<HTMLButtonElement>(`.${ACCEPT_BUTTON_CLASS}`)?.classList[
        action
    ](HIGHLIGHT_ACCEPT_CLASS)
    card.querySelector<HTMLButtonElement>(
        `.${DECLINE_BUTTON_CLASS}`
    )?.classList[action](HIGHLIGHT_DECLINE_CLASS)
}

const SPECIAL_CHAR_PATTERN = /[^a-zA-Z0-9\s]/g
export const countSpecialChars = (text: string): number => (text.match(SPECIAL_CHAR_PATTERN) ?? []).length

/** Gets the plain text of CKEditor content (handles text nodes, elements, and document fragments) */
const getContentText = (content: any): string => {
    if (!content?.is) return ''
    if (content.is('$text')) return content.data ?? ''
    if (content.is('documentFragment') || content.is('element')) {
        let text = ''
        for (const node of content.getChildren?.() || []) {
            text += getContentText(node)
        }
        return text
    }
    return ''
}

/** Gets the plain text length of CKEditor content (handles text nodes, elements, and document fragments) */
const getContentTextLength = (content: any): number => {
    if (!content?.is) return 0
    if (content.is('$text')) return content.data?.length || 0
    // Handle containers: documentFragment, paragraph, span, or any element with children
    if (content.is('documentFragment') || content.is('element')) {
        let length = 0
        for (const node of content.getChildren?.() || []) {
            length += getContentTextLength(node)
        }
        return length
    }
    return 0
}

/**
 * Sets up special character limit on a CKEditor instance.
 * Prevents input when the special character count would be exceeded.
 */
export const setupEditorSpecialCharLimit = (
    editor: { model: { on: (event: string, callback: (evt: any, args: any[]) => void, options?: any) => void; document: { selection: any } }; getData: () => string },
    maxSpecialChars: number
): void => {
    editor.model.on(
        'insertContent',
        (evt: { stop: () => void }, args: any[]) => {
            const content = args[0]
            const currentText = sanitizeAndExtractText(editor.getData())
            const currentSpecialCount = countSpecialChars(currentText)

            // Subtract special chars from any selected text that will be replaced by the insertion
            const selection = editor.model.document.selection
            let selectedSpecialCount = 0
            if (!selection.isCollapsed) {
                let selectedText = ''
                for (const range of selection.getRanges()) {
                    for (const item of range.getItems()) {
                        if (item.is('$text') || item.is('$textProxy')) {
                            selectedText += item.data
                        }
                    }
                }
                selectedSpecialCount = countSpecialChars(selectedText)
            }

            const insertText = getContentText(content)
            const insertSpecialCount = countSpecialChars(insertText)

            if (currentSpecialCount - selectedSpecialCount + insertSpecialCount > maxSpecialChars) {
                evt.stop()
            }
        },
        { priority: 'highest' }
    )
}

/**
 * Sets up character limit on a CKEditor instance.
 * Prevents input when limit would be exceeded.
 */
export const setupEditorCharacterLimit = (
    editor: { model: { on: Function }; getData: () => string },
    maxLength: number
): void => {
    editor.model.on(
        'insertContent',
        (evt: { stop: () => void }, [content]: [any]) => {
            const currentLength = sanitizeAndExtractText(editor.getData()).length
            const insertLength = getContentTextLength(content)

            if (currentLength + insertLength > maxLength) {
                evt.stop()
            }
        },
        { priority: 'highest' }
    )
}
